import moment from 'moment-timezone';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import constants from 'components/common/Constants/constants';
import { ColoredDotCell } from 'components/common/Tables/Cells/ColoredDotCell';
import { LatestUpdateCell } from 'components/common/Tables/Cells/LatestUpdateCell';
import { PillsCell } from 'components/common/Tables/Cells/PillsCell';
import { QualityScoreCell } from 'components/common/Tables/Cells/QualityScoreCell';
import { RiskScoreCell } from 'components/common/Tables/Cells/RiskScoreCell';
import { SPICell } from 'components/common/Tables/Cells/SPICell';
import { DefaultColumnFilter } from 'components/common/Tables/ColumnFilter/ColumnFilter';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';

import { FinishDateCell } from 'components/Dashboards/Portfolio/PortfolioOverview/cells/finishDate';
import { ProjectCell } from 'components/Dashboards/Portfolio/PortfolioOverview/cells/projectCell';
import { ScheduleAdherenceCell } from 'components/Dashboards/Portfolio/PortfolioOverview/cells/scheduleAdherence';
import { SubmittedCompletionDurationCell } from 'components/Dashboards/Portfolio/PortfolioOverview/cells/submittedCompletionDuration';
import { getColorFromStatus, getElementByName } from 'components/Dashboards/Portfolio/PortfolioOverview/helper';

export const columns = [
    {
        Header: 'Project / Sub-program',
        ...columnsCommonConfig.projectNameSubProgram,
        accessor: (rowOriginal) => `${rowOriginal.project.name} / ${rowOriginal.contract.name}`,
        width: 300,
        size: 300,
        Filter: ({ column }) => <DefaultColumnFilter placeholder={'Search by name'} column={column} />,
        Cell: ({ row }) => {
            const original = row.original;
            const title = `${original.project.name} / ${original.contract.name}`;
            return <ProjectCell title={title} versionHashCode={original.version_hash_code} />;
        },
    },
    {
        Header: 'SPI',
        ...columnsCommonConfig.spi,
        accessor: 'spi.value',
        Cell: ({ value }) => {
            return <SPICell value={value} />;
        },
        sortType: 'basic',
    },
    {
        Header: 'Planned (%)',
        ...columnsCommonConfig.criticalityWeightedPlanned,
        accessor: 'criticality_weighted_planned',
        Cell: ({ value }) => <div>{value}%</div>,
    },
    {
        Header: 'Actual (%)',
        ...columnsCommonConfig.criticalityWeightedActual,
        accessor: 'criticality_weighted_actual',
        Cell: ({ value }) => <div>{value}%</div>,
    },
    {
        Header: 'Actual Start',
        ...columnsCommonConfig.actualStartDate,
        accessor: 'actual_start_date',
        Cell: ({ value }) => <div>{moment(value).format(constants.formats.date.default)}</div>,
    },
    {
        Header: 'Finish Date',
        ...columnsCommonConfig.finishDate,
        accessor: (rowOriginal) => getElementByName(rowOriginal.finish_date.value, 'date').value,
        Cell: ({ row }) => <FinishDateCell row={row} />,
    },
    {
        Header: 'Baseline Finish',
        ...columnsCommonConfig.baselineFinishDate,
        accessor: 'baseline_finish_date',
        Cell: ({ value }) => <div>{moment(value).format(constants.formats.date.default)}</div>,
    },
    {
        Header: 'Quality Score',
        ...columnsCommonConfig.qualityNumeric,
        accessor: 'quality_numeric',
        Cell: ({ value }) => {
            return <QualityScoreCell value={value.value} />;
        },
    },
    {
        Header: 'Latest Update',
        ...columnsCommonConfig.latestVersion,
        accessor: 'last_version_date',
        Cell: ({ row }) => {
            const original = row.original;
            const lastVersionDate = moment(original.last_version_date).format(constants.formats.date.default);
            const baselineVersionDate = moment(original.baseline_version_date).format(constants.formats.date.default);

            return <LatestUpdateCell baselineVersionDate={baselineVersionDate} lastVersionDate={lastVersionDate} />;
        },
    },
    {
        Header: 'Program Tags',
        ...columnsCommonConfig.programTags,
        accessor: (rowOriginal) => rowOriginal.tagList.value.map((item) => item.value).join(','),
        Cell: ({ row }) => {
            const list = row.original.tagList.value.map((item) => item.value);
            return <PillsCell list={list} type={'tag'} />;
        },
    },
    {
        Header: 'Risk Score',
        ...columnsCommonConfig.riskNumeric,
        accessor: 'risk_numeric',
        Cell: ({ value }) => {
            return <RiskScoreCell value={value.value} />;
        },
    },
    {
        Header: 'Progress',
        ...columnsCommonConfig.progress,
        accessor: (rowOriginal) => rowOriginal.progress.value.toFixed(2),
        Cell: ({ row }) => {
            const progress = row.original.progress;
            return (
                <ColoredDotCell
                    color={getColorFromStatus(progress.fluxIndex, 'Progress')}
                    toolTipContent={String(progress.value)}
                />
            );
        },
    },
    {
        Header: 'Risk',
        ...columnsCommonConfig.risk,
        accessor: (rowOriginal) => rowOriginal.risk.value.toFixed(2),
        Cell: ({ row }) => {
            const risk = row.original.risk;
            return (
                <ColoredDotCell
                    color={getColorFromStatus(risk.fluxIndex, 'Risk')}
                    toolTipContent={String(risk.value)}
                />
            );
        },
    },
    {
        Header: 'Quality',
        ...columnsCommonConfig.quality,
        accessor: (rowOriginal) => rowOriginal.quality.value.toFixed(2),
        Cell: ({ row }) => {
            const quality = row.original.quality;

            return (
                <ColoredDotCell
                    color={getColorFromStatus(quality.fluxIndex, 'Quality')}
                    toolTipContent={String(quality.value)}
                />
            );
        },
    },
    {
        Header: 'Submitted Completion (Total Duration)',
        ...columnsCommonConfig.submittedCompletionDate,
        accessor: (rowOriginal) => Number(rowOriginal.submitted_completion.value).toFixed(2),
        Cell: ({ row }) => <SubmittedCompletionDurationCell row={row} />,
    },
    {
        Header: 'Schedule Performance (Criticality Weighted)',
        ...columnsCommonConfig.criticalityAdherence,
        accessor: (rowOriginal) =>
            Number(getElementByName(rowOriginal.criticality_adherence.value, 'result').value).toFixed(2),
        Cell: ({ row }) => {
            const criticalityAdherence = row.original.criticality_adherence;
            const fluxIndex = getElementByName(criticalityAdherence.value, 'result').fluxIndex;
            return (
                <ScheduleAdherenceCell
                    value={criticalityAdherence.value}
                    color={getColorFromStatus(fluxIndex, 'CriticalityAdherence')}
                />
            );
        },
    },
    {
        Header: 'Schedule Performance (Completion Weighted)',
        ...columnsCommonConfig.milestoneAdherence,
        accessor: (rowOriginal) =>
            Number(getElementByName(rowOriginal.milestone_adherence.value, 'result').value).toFixed(2),
        Cell: ({ row }) => {
            const milestoneAdherence = row.original.milestone_adherence;
            const fluxIndex = getElementByName(milestoneAdherence.value, 'result').fluxIndex;
            return (
                <ScheduleAdherenceCell
                    value={milestoneAdherence.value}
                    color={getColorFromStatus(fluxIndex, 'MilestoneAdherence')}
                />
            );
        },
    },
];

export const initialState: ITableColumnState = {
    sortBy: [{ id: 'projectNameSubProgram' }],
    hiddenColumns: [
        'riskNumeric',
        'progress',
        'risk',
        'quality',
        'submittedCompletionDate',
        'criticalityAdherence',
        'milestoneAdherence',
    ],
};
